import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api } from '../services/api';
import { Loading } from '../components/Loading';
import { CoverImage } from '../components/CoverImage';
import type { Novel } from '../services/types';

function NovelCard({ novel }: { novel: Novel }) {
  return (
    <Link
      to={`/novels/${novel.id}`}
      className="block bg-white rounded-lg shadow-sm hover:shadow-md transition-all hover:-translate-y-1 overflow-hidden"
    >
      <div className="aspect-[3/4] bg-gray-100 relative">
        <CoverImage
          src={novel.cover_url}
          alt={novel.title}
          className="w-full h-full object-cover"
          loading="lazy"
          fallback={
            <div className="absolute inset-0 bg-gradient-to-br from-primary-100 to-primary-200 flex items-center justify-center">
              <span className="text-4xl text-primary-400">📖</span>
            </div>
          }
        />
      </div>
      <div className="p-3">
        <h3 className="font-medium text-gray-800 line-clamp-2 text-sm leading-tight mb-1">
          {novel.title}
        </h3>
        <p className="text-xs text-gray-400 mt-1">
          {novel.total_chapters} 章
        </p>
      </div>
    </Link>
  );
}

export function AuthorPage() {
  const { authorName } = useParams<{ authorName: string }>();
  const decodedAuthorName = authorName ? decodeURIComponent(authorName) : '';

  const [novels, setNovels] = useState<Novel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (decodedAuthorName) {
      loadAuthorNovels();
    }
  }, [decodedAuthorName]); // loadAuthorNovels is not stable, but decodedAuthorName change should trigger reload

  const loadAuthorNovels = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.getNovelsByAuthor(decodedAuthorName);
      setNovels(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : '載入失敗');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      {/* Author Header */}
      <div className="relative mb-8 py-8 px-6 bg-gradient-to-r from-gray-800 to-gray-900 rounded-2xl overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-primary-500/10 rounded-full blur-3xl transform translate-x-1/2 -translate-y-1/2" />
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-primary-600/10 rounded-full blur-2xl transform -translate-x-1/2 translate-y-1/2" />

        <div className="relative z-10">
          <p className="text-primary-400 text-sm font-medium mb-2 tracking-wider uppercase">
            作者專頁
          </p>
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-3">
            {decodedAuthorName}
          </h1>
          <p className="text-gray-400">
            共 {novels.length} 部作品
          </p>
        </div>
      </div>

      {/* Breadcrumb */}
      <nav className="flex items-center gap-2 text-sm text-gray-500 mb-6">
        <Link to="/" className="hover:text-primary-600 transition-colors">首頁</Link>
        <span>/</span>
        <span className="text-gray-800">{decodedAuthorName}</span>
      </nav>

      {/* Content */}
      {loading ? (
        <Loading text="載入中..." />
      ) : error ? (
        <div className="text-center py-12">
          <p className="text-red-500 mb-4">{error}</p>
          <button
            onClick={loadAuthorNovels}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            重試
          </button>
        </div>
      ) : novels.length === 0 ? (
        <div className="text-center py-16 bg-gray-50 rounded-xl">
          <div className="text-6xl mb-4">📚</div>
          <p className="text-gray-500 mb-2">找不到此作者的作品</p>
          <Link
            to="/"
            className="text-primary-600 hover:text-primary-700 text-sm"
          >
            返回首頁
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {novels.map((novel) => (
            <NovelCard key={novel.id} novel={novel} />
          ))}
        </div>
      )}
    </div>
  );
}
